"use client"
import { useTranslations } from "next-intl"

import { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { createClient } from "@/utils/supabase/client"
import { getAvatarUrl } from "@/lib/getAvatarUrl"
import { SQUARE_SM } from "@/lib/images/presets"
import { cn } from "@/lib/utils"
import {
  SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
  SEARCHABLE_DROPDOWN_OPTION_CLASS,
  searchableDropdownPanelClass,
} from "@/lib/dropdown-styles"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { MessageCircle, Edit3, Search as SearchIcon, User, X, Trash2, MoreVertical, Flag } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { useConversationUnreadStatuses } from "@/hooks/use-conversation-unread-statuses"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ChatInboxSkeleton } from "./ChatInboxSkeleton"
import { inboxFloatingSearchFadeLayerClassName, inboxFloatingSearchFadeStyle } from "./inbox-floating-search-styles"
import { ReportDialog } from "@/components/ui/report-dialog"
import { resolveChatReportTarget, type ChatReportTarget } from "@/lib/chat-report"
import { useToast } from "@/hooks/use-toast"
import { upsertChatRoom } from "@/lib/chat/chat-upsert"

type ConversationRow = {
  id: string
  room_name: string
  conversation_participants: { user_id: string }[]
  messages: { content: string; created_at: string; sender_id: string }[]
}

type Profile = { id: string; username: string; avatar_url?: string | null }

const DAY_IN_MS = 86_400_000
const startOfDay = (input: Date) => new Date(input.getFullYear(), input.getMonth(), input.getDate())

const formatConversationTimestamp = (dateString?: string) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ""
  const now = new Date()
  const diffDays = Math.floor((startOfDay(now).getTime() - startOfDay(date).getTime()) / DAY_IN_MS)
  let label = ""
  if (diffDays === 0) label = "Today"
  else if (diffDays > 0 && diffDays < 7) label = `${diffDays} day${diffDays === 1 ? "" : "s"} ago`
  else if (now.getFullYear() === date.getFullYear()) label = date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  else label = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  return `${label} ${time}`
}

// État de swipe pour supprimer
interface SwipingState {
  id: string;
  offset: number;
}

type ReportTarget = ChatReportTarget

const canonicalRoomKey = (roomName: string) => roomName.split(":").sort().join(":")

export function ChatInboxList({
  currentUserId,
  onOpenConversationAction,
  onComposeAction,
  onCloseAction,
  isDialog = false,
  layout = "default",
  activeRoomName,
  onHasConversationsChange,
}: {
  currentUserId: string
  onOpenConversationAction: (roomName: string, draft?: any, otherProfile?: { username: string; avatar_url?: string | null }) => void
  onComposeAction?: () => void
  onCloseAction?: () => void
  isDialog?: boolean
  layout?: "default" | "split"
  activeRoomName?: string | null
  onHasConversationsChange?: (hasConversations: boolean) => void
}) {
  const t = useTranslations("chat");
  const formatConversationTimestamp = (timestamp: string): string => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t("today");
    if (diffDays === 1) return then.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diffDays < 7) return t("daysAgo", { count: diffDays });
    return then.toLocaleDateString();
  };
  // Module-level cache to keep inbox state seamless across unmount/mount cycles (e.g., when returning from a chat)
  // Cache structure kept outside component scope by using a static on module (closure at file scope)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const inboxCacheRef = useRef<{ rows: ConversationRow[]; profiles: Record<string, { username: string; avatar_url?: string | null }>; timestamp: number } | null>(null)
  const supabase = useMemo(() => createClient(), [])
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<ConversationRow[]>([])
  const [profiles, setProfiles] = useState<Record<string, { username: string; avatar_url?: string | null }>>({})
  const [search, setSearch] = useState("")
  const [swipingItem, setSwipingItem] = useState<SwipingState | null>(null)
  const unreadByRoom = useConversationUnreadStatuses(currentUserId)
  const [optimisticUnread, setOptimisticUnread] = useState<Record<string, boolean>>({})
  const [lastFetchTime, setLastFetchTime] = useState(0)
  const inFlightRef = useRef<AbortController | null>(null)
  const rtChannelRef = useRef<any>(null)
  const roomChannelsRef = useRef<Map<string, any>>(new Map())
  const refreshTimerRef = useRef<number | null>(null)
  const searchInputId = "inbox-search-input"
  const startX = useRef(0)
  const SWIPE_ACTION_WIDTH = 80
  const MAX_SWIPE_OFFSET = -(SWIPE_ACTION_WIDTH * 2)
  const [searchFocused, setSearchFocused] = useState(false)
  const [searchResults, setSearchResults] = useState<Profile[]>([])
  const [searchingUsers, setSearchingUsers] = useState(false)
  const suggestionsRef = useRef<HTMLDivElement | null>(null)
  const suggestionsVisible = searchFocused && search.trim().length >= 2
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null)
  const [reportingRoom, setReportingRoom] = useState<string | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    if (!onHasConversationsChange || loading) return
    onHasConversationsChange(rows.length > 0)
  }, [loading, rows.length, onHasConversationsChange])

  useEffect(() => {
    let mounted = true
    let intervalId: NodeJS.Timeout | null = null

    // Use cached data instantly if available and fresh (<= 30s)
    const cached = (ChatInboxList as any).__cache as { rows: ConversationRow[]; profiles: Record<string, { username: string; avatar_url?: string | null }>; timestamp: number } | undefined
    const isCacheFresh = cached && (Date.now() - cached.timestamp) <= 30_000
    if (isCacheFresh) {
      setRows(cached!.rows)
      setProfiles(cached!.profiles)
      setLoading(false)
    }
    
  const fetchConversations = async (showLoading = true) => {
      // Avoid flooding: skip if tab hidden
      if (document.hidden) return
      // Coalesce: if a request is in-flight, abort it (we prefer latest)
      if (inFlightRef.current) {
        try { inFlightRef.current.abort() } catch {}
        inFlightRef.current = null
      }
      // Throttle background refreshes to at most once every 8s
      if (!showLoading && Date.now() - lastFetchTime < 8000) return
      if (showLoading) setLoading(true)
      try {
        const ctrl = new AbortController()
        inFlightRef.current = ctrl
        const res = await fetch(`/api/chat/inbox`, { signal: ctrl.signal })
        if (!res.ok) throw new Error('Inbox API failed')
        const json = await res.json()
        const list = (json.conversations || []) as Array<any>

        // Adapt to ConversationRow so UI code stays minimal
        const adapted: ConversationRow[] = list.map((row: any) => ({
          id: row.conversation_id,
          room_name: row.room_name,
          conversation_participants: [{ user_id: currentUserId }, ...(row.other_user_id ? [{ user_id: row.other_user_id }] : [])],
          messages: row.last_message_created_at ? [{ content: row.last_message_content, created_at: row.last_message_created_at, sender_id: row.last_sender_id }] : [],
        }))

        // Build profiles map from payload
        const profs: Record<string, { username: string; avatar_url?: string | null }> = {}
        for (const row of list) {
          if (row.other_user_id) {
            profs[row.other_user_id] = { username: row.other_username || t("unknown"), avatar_url: row.other_avatar_url || null }
          }
        }
        
        if (mounted) {
          setRows(adapted)
          setProfiles(profs)
          setLastFetchTime(Date.now())
          // Update module cache
          ;(ChatInboxList as any).__cache = { rows: adapted, profiles: profs, timestamp: Date.now() }
          // Ensure broadcast subscriptions for fast updates
          try {
            const names = Array.from(new Set(adapted.map(r => (r.room_name.includes(':') ? r.room_name.split(':').sort().join(':') : r.room_name))))
            ensureRoomSubscriptions(names)
          } catch {}
        }
      } catch (e) {
        if (mounted) setRows([])
      } finally {
        if (mounted && showLoading) setLoading(false)
        inFlightRef.current = null
      }
    }

    // Initial fetch: use loading only if no fresh cache
    fetchConversations(!isCacheFresh)

    // Subscribe to realtime DB changes: refresh inbox when a new message arrives
    const channel = supabase
      .channel(`inbox-updates-${currentUserId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload: any) => {
          try {
            const m = payload?.new as { room_name?: string; content?: string; created_at?: string; sender_id?: string } | undefined
            if (!m?.room_name) return
            const room = m.room_name
            const canonicalRoom = room.includes(':') ? room.split(':').sort().join(':') : room

            // Mark as unread optimistically if the message is from the other user
            if (m.sender_id && m.sender_id !== currentUserId) {
              setOptimisticUnread(prev => ({ ...prev, [canonicalRoom]: true }))
            }

            // Optimistic in-memory update: move/update the affected row to top instantly
            let isKnownConversation = false
            setRows(prev => {
              const idx = prev.findIndex(r => r.room_name === canonicalRoom)
              // If not currently in list, only add a placeholder if it likely concerns us
        const isUserParticipant = room.includes(':') && room.split(':').includes(currentUserId)
              const shouldInsertPlaceholder = idx === -1 && (isUserParticipant || (m.sender_id === currentUserId))
              if (shouldInsertPlaceholder) {
                // Unknown conversation: insert a lightweight placeholder row immediately
                const parts = room.split(':')
                const otherId = parts.find(p => p !== currentUserId) || ''
                const placeholder: ConversationRow = {
          id: canonicalRoom, // temporary id until reconciled
          room_name: canonicalRoom,
                  conversation_participants: [{ user_id: currentUserId }, { user_id: otherId }],
                  messages: [{ content: m.content || '', created_at: m.created_at || new Date().toISOString(), sender_id: m.sender_id || otherId }],
                }
                return [placeholder, ...prev]
              }
              if (idx !== -1) {
                isKnownConversation = true
              } else {
                // Unknown and we didn't insert a placeholder (not confident it's ours) -> keep list as-is
                return prev
              }
              const updated: ConversationRow = {
                ...prev[idx],
                messages: [{ content: m.content || prev[idx].messages?.[0]?.content || '', created_at: m.created_at || new Date().toISOString(), sender_id: m.sender_id || prev[idx].messages?.[0]?.sender_id || '' }]
              }
              const next = [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)]
              return next
            })

            // If we already knew about this conversation, skip the immediate reconciliation fetch
            // to avoid extra network calls; the UI is already updated. Unknown conversations will reconcile below.
            if (isKnownConversation) return
          } catch {}
          // Debounce real fetch to reconcile (handles new conversations, profile changes, etc.)
          if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current)
          refreshTimerRef.current = window.setTimeout(() => {
            fetchConversations(false)
          }, 180)
        }
      )
      .subscribe()
    rtChannelRef.current = channel

    // Fallback polling every 60s (in case realtime disconnects)
    intervalId = setInterval(() => {
      if (!mounted) return
      if (document.hidden) return
      fetchConversations(false)
    }, 60000)

    // Also listen for visibility changes to refetch when user comes back
    const handleVisibilityChange = () => {
      if (!document.hidden && mounted) {
        fetchConversations(false) // false = no loading animation
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => { 
      mounted = false
      if (intervalId) clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (rtChannelRef.current) {
        try { supabase.removeChannel(rtChannelRef.current) } catch {}
        rtChannelRef.current = null
      }
      // Cleanup all broadcast room channels
      Array.from(roomChannelsRef.current.entries()).forEach(([name, ch]) => {
        try { supabase.removeChannel(ch) } catch {}
        roomChannelsRef.current.delete(name)
      })
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }
  }, [currentUserId, supabase])

  const ensureRoomSubscriptions = useCallback((roomNames: string[]) => {
    // Subscribe to broadcasts for each room for immediate updates
    const desired = new Set(roomNames)

    // Unsubscribe channels no longer needed
    Array.from(roomChannelsRef.current.entries()).forEach(([name, ch]) => {
      if (!desired.has(name)) {
        try { supabase.removeChannel(ch) } catch {}
        roomChannelsRef.current.delete(name)
      }
    })

    // Subscribe to new rooms
    for (const rawName of roomNames) {
      const name = rawName.includes(':') ? rawName.split(':').sort().join(':') : rawName
      if (roomChannelsRef.current.has(name)) continue

      const ch = supabase
        .channel(name)
        .on('broadcast', { event: 'message' }, (payload: any) => {
          const msg = payload?.payload as { content?: string; createdAt?: string; senderId?: string }
          if (!msg) return
          const createdAt = msg.createdAt || new Date().toISOString()

          // Optimistic unread for messages from other user
          if (msg.senderId && msg.senderId !== currentUserId) {
            setOptimisticUnread(prev => ({ ...prev, [name]: true }))
          }

          // Move/update the row immediately
          setRows(prev => {
            const idx = prev.findIndex(r => r.room_name === name)
            if (idx === -1) {
              // Unknown: create placeholder using room name parts
              const parts = name.split(':')
              const otherId = parts.find(p => p !== currentUserId) || ''
              const placeholder: ConversationRow = {
                id: name,
                room_name: name,
                conversation_participants: [{ user_id: currentUserId }, { user_id: otherId }],
                messages: [{ content: msg.content || '', created_at: createdAt, sender_id: msg.senderId || otherId }],
              }
              return [placeholder, ...prev]
            }
            const updated: ConversationRow = {
              ...prev[idx],
              messages: [{ content: msg.content || prev[idx].messages?.[0]?.content || '', created_at: createdAt, sender_id: msg.senderId || prev[idx].messages?.[0]?.sender_id || '' }],
            }
            return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)]
          })
        })
        .subscribe()

      roomChannelsRef.current.set(name, ch)
    }
  }, [supabase, currentUserId])

  // Keep subscriptions in sync with current rows as they change (handles placeholders/new convos)
  useEffect(() => {
    try {
      const names = Array.from(new Set(rows.map(r => (r.room_name.includes(':') ? r.room_name.split(':').sort().join(':') : r.room_name))))
      ensureRoomSubscriptions(names)
    } catch {}
  }, [rows, ensureRoomSubscriptions])

  useEffect(() => {
    if (!suggestionsVisible) {
      setSearchResults([])
      setSearchingUsers(false)
      return
    }
    let cancelled = false
    setSearchingUsers(true)
    const term = search.trim().toLowerCase()
    const timer = window.setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('get_public_profiles_with_followers', {
          p_current_user: currentUserId
        })
        if (error || !Array.isArray(data)) throw error
        if (cancelled) return
        const filtered = (data as Profile[])
          .filter((profile) => profile.id !== currentUserId && profile.username.toLowerCase().includes(term))
          .slice(0, 5)
        setSearchResults(filtered)
      } catch {
        if (!cancelled) setSearchResults([])
      } finally {
        if (!cancelled) setSearchingUsers(false)
      }
    }, 200)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [suggestionsVisible, search, supabase, currentUserId])

  const handleSelectProfileFromSearch = useCallback((profile: Profile) => {
    const roomName = [currentUserId, profile.id].sort().join(':')
    setSearch("")
    setSearchResults([])
    setSearchFocused(false)
    onOpenConversationAction(roomName, undefined, { username: profile.username, avatar_url: profile.avatar_url ?? null })
    upsertChatRoom({ roomName, participantIds: [currentUserId, profile.id] }).catch(() => {})
  }, [currentUserId, onOpenConversationAction])

  // When server unread state flips to false, drop optimistic flag
  useEffect(() => {
    setOptimisticUnread(prev => {
      const next = { ...prev }
      for (const room in next) {
        if (!unreadByRoom[room]) delete next[room]
      }
      return next
    })
  }, [unreadByRoom])

  // Fonctions de gestion du swipe
  const handleTouchStart = (e: React.TouchEvent, conversationId: string) => {
    if (swipingItem && swipingItem.id !== conversationId) {
      setSwipingItem(null)
    }
    setSwipingItem({ id: conversationId, offset: swipingItem?.id === conversationId ? swipingItem.offset : 0 })
    startX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent, conversationId: string) => {
    if (!swipingItem || swipingItem.id !== conversationId) return
    const currentX = e.touches[0].clientX
    let diff = currentX - startX.current
    if (swipingItem.offset < 0) {
      diff += swipingItem.offset
    }
    const newOffset = Math.max(MAX_SWIPE_OFFSET, Math.min(0, diff))
    setSwipingItem({ ...swipingItem, offset: newOffset })
  }

  const handleTouchEnd = (conversationId: string) => {
    if (!swipingItem || swipingItem.id !== conversationId) return
    if (swipingItem.offset < MAX_SWIPE_OFFSET / 2) {
      setSwipingItem({ ...swipingItem, offset: MAX_SWIPE_OFFSET })
    } else {
      setSwipingItem({ ...swipingItem, offset: 0 })
    }
  }

  // Soft delete conversation (optimized)
  const handleDeleteConversation = async (roomName: string) => {
    // Optimistic update: remove immediately from UI
    setRows(prev => prev.filter(row => row.room_name !== roomName))
    setSwipingItem(null)
    
    try {
      const response = await fetch('/api/chat/soft-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomName })
      })
      
      if (!response.ok) {
        // If failed, revert the optimistic update
        console.error('Failed to delete conversation, reverting...')
        // Refresh the list to revert changes
        window.location.reload()
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      // Revert on error
      window.location.reload()
    }
  }

  const handleReportChat = useCallback(async (roomName: string, otherId?: string, otherProfile?: { username?: string; avatar_url?: string | null }) => {
    if (!roomName) return
    setReportingRoom(roomName)
    try {
      const result = await resolveChatReportTarget(roomName, currentUserId, otherProfile, otherId)
      if ("kind" in result) {
        if (result.kind === "no_messages") {
          toast({ title: "No messages to report", description: "This chat has no messages yet." })
          return
        }
        if (result.kind === "no_other_messages") {
          toast({ title: "No messages to report", description: "No messages from the other user yet." })
          return
        }
        toast({ title: t("failedReport"), description: result.message || t("tryAgain") })
        return
      }
      setReportTarget(result)
    } catch (err: any) {
      toast({ title: t("failedReport"), description: err?.message || t("tryAgain") })
    } finally {
      setReportingRoom(null)
    }
  }, [currentUserId, t, toast])

  // Reset swipe when clicking outside
  const handleInboxClick = (e: React.MouseEvent) => {
    if (!swipingItem) return
    const target = e.target as HTMLElement
    const stillOnItem = target.closest(`.conversation-item-${swipingItem.id}`)
    const onDeleteBtn = target.closest(`.delete-button-${swipingItem.id}`)
    const onReportBtn = target.closest(`.report-button-${swipingItem.id}`)
    if (!stillOnItem && !onDeleteBtn && !onReportBtn) {
      setSwipingItem(null)
    }
  }

  if (loading) {
    return (
      <ChatInboxSkeleton
        layout={layout}
        onComposeAction={() => {
          if (onComposeAction) onComposeAction()
          setSearch("")
        }}
        onCloseAction={onCloseAction}
      />
    )
  }

  const searchBlock = (
    <div className="relative">
      <SearchIcon className={cn("absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-20", layout === "split" ? "left-4" : "left-4")} />
      <Input
        id={searchInputId}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("search")}
        className={cn(
          "pl-10 relative",
          layout === "split"
            ? "h-12 min-h-12 w-full rounded-full border border-border/50 bg-background/70 pr-4 text-base shadow-sm backdrop-blur-sm focus-visible:ring-1 focus-visible:ring-ring"
            : "rounded-full h-10 min-h-10"
        )}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => {
          setTimeout(() => {
            const active = document.activeElement as HTMLElement | null
            if (suggestionsRef.current && active && suggestionsRef.current.contains(active)) return
            setSearchFocused(false)
          }, 0)
        }}
      />
      {suggestionsVisible && (
        <div
          ref={suggestionsRef}
          className={cn(
            "absolute left-0 right-0 z-1500 overflow-hidden p-2 shadow-2xl",
            searchableDropdownPanelClass(),
            layout === "split" ? "top-14" : "top-12"
          )}
        >
          {searchingUsers ? (
            <div className={cn("px-4 py-3 text-base", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>{t("searching")}</div>
          ) : searchResults.length === 0 ? (
            <div className={cn("px-4 py-3 text-base", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>{t("noUsers")}</div>
          ) : (
            searchResults.map((profile) => (
              <button
                key={profile.id}
                type="button"
                className={cn("flex w-full items-center gap-3 text-left", SEARCHABLE_DROPDOWN_OPTION_CLASS)}
                onClick={() => handleSelectProfileFromSearch(profile)}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={getAvatarUrl(profile.avatar_url || "") || undefined} alt={profile.username} />
                  <AvatarFallback>{profile.username.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm font-medium">{profile.username}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )

  const conversationList = (() => {
          // Filtrer les conversations si on est en mode recherche
          const filteredRows = search.trim().length >= 2 
            ? rows.filter(r => {
                const otherId = r.conversation_participants?.map(p => p.user_id).find(id => id !== currentUserId)
                const other = otherId ? profiles[otherId] : undefined
                const name = other?.username || ''
                return name.toLowerCase().includes(search.trim().toLowerCase())
              })
            : rows

          if (filteredRows.length === 0) {
            return search.trim().length >= 2 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t("noMatchingConversations")}</h3>
                <p className="text-muted-foreground">{t("tryDifferentName")}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t("noConversationsYet")}</h3>
                <p className="text-muted-foreground">{t("startConversation")}</p>
              </div>
            )
          }

          const activeKey = activeRoomName ? canonicalRoomKey(activeRoomName) : null

          return (
            <div className="divide-y">
              {filteredRows.map((r) => {
                const last = r.messages?.[0]
                const otherId = r.conversation_participants?.map(p => p.user_id).find(id => id !== currentUserId)
                const other = otherId ? profiles[otherId] : undefined
                const name = other?.username || (otherId ? otherId.slice(0, 6) : t("unknown"))
                // Single render site below (`h-10 w-10` row) -> SQUARE_SM.
                const avatar = getAvatarUrl(other?.avatar_url || '', SQUARE_SM) || undefined
                const conversationId = r.id
                const isUnread = optimisticUnread[r.room_name] || unreadByRoom[r.room_name]
                const isActive = activeKey !== null && canonicalRoomKey(r.room_name) === activeKey
                
                return (
                  <div 
                    key={r.id} 
                    className={cn("relative overflow-hidden", `conversation-item-${conversationId}`)}
                  >
                    {/* Inline options menu (desktop only) */}
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 z-1 hidden md:block">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={(e) => e.stopPropagation()}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" surface="glass">
                            <DropdownMenuItem
                              className="text-red-500 focus:text-red-500"
                              disabled={!otherId || reportingRoom === r.room_name}
                              onSelect={(e) => {
                                e.stopPropagation()
                                if (!otherId || reportingRoom === r.room_name) return
                                handleReportChat(r.room_name, otherId, other)
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (!otherId || reportingRoom === r.room_name) return
                                handleReportChat(r.room_name, otherId, other)
                              }}
                            >
                              <Flag className="h-4 w-4 mr-2" /> {t("reportChat")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-500 focus:text-red-500"
                              onSelect={(e) => { e.stopPropagation(); handleDeleteConversation(r.room_name) }}
                              onClick={(e) => { e.stopPropagation(); handleDeleteConversation(r.room_name) }}
                            >
                            <Trash2 className="h-4 w-4 mr-2" /> {t("deleteConversation")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {/* Swipe actions (mobile) */}
                    <div
                      className={cn(
                        `swipe-actions-${conversationId} absolute inset-y-0 right-0 flex transition-all duration-300 ease-out`,
                        "focus:outline-hidden",
                      )}
                      style={{
                        width: `${Math.abs(MAX_SWIPE_OFFSET)}px`,
                        transform: `translateX(${swipingItem?.id === conversationId && swipingItem.offset < 0 ? "0%" : "100%"})`,
                        opacity: swipingItem?.id === conversationId && swipingItem.offset < 0 ? 1 : 0,
                        visibility: swipingItem?.id === conversationId && swipingItem.offset < 0 ? "visible" : "hidden",
                        pointerEvents: swipingItem?.id === conversationId && swipingItem.offset < 0 ? "auto" : "none",
                      }}
                    >
                      <button
                        className={cn(
                          `report-button-${conversationId} w-20 bg-amber-500 text-white flex items-center justify-center transition-colors`,
                          "focus:outline-hidden focus:ring-2 focus:ring-amber-400 focus:ring-offset-2",
                          (!otherId || reportingRoom === r.room_name) && "opacity-60 cursor-not-allowed"
                        )}
                        disabled={!otherId || reportingRoom === r.room_name}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!otherId || reportingRoom === r.room_name) return
                          setSwipingItem(null)
                          handleReportChat(r.room_name, otherId, other)
                        }}
                        aria-label={t("reportChatWith", { name })}
                      >
                        <Flag className="h-5 w-5" />
                      </button>
                      <button
                        className={cn(
                          `delete-button-${conversationId} w-20 bg-red-600 text-white flex items-center justify-center transition-colors`,
                          "focus:outline-hidden focus:ring-2 focus:ring-red-400 focus:ring-offset-2",
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSwipingItem(null)
                          handleDeleteConversation(r.room_name)
                        }}
                        aria-label={t("deleteConversationWith", { name })}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Conversation item */}
                    <button
                      onClick={(e) => {
                        // Gérer Cmd+Click pour ouvrir dans un nouvel onglet sur desktop
                        if (isDialog && (e.metaKey || e.ctrlKey)) {
                          // Ouvrir dans un nouvel onglet
                          window.open(`/chat/${encodeURIComponent(r.room_name)}`, '_blank')
                        } else {
                          // Comportement normal
                          // Clear optimistic unread immediately upon opening
                          setOptimisticUnread(prev => {
                            if (!prev[r.room_name]) return prev
                            const { [r.room_name]: _omit, ...rest } = prev
                            return rest
                          })
                          onOpenConversationAction(r.room_name, undefined, other ? { username: other.username, avatar_url: other.avatar_url } : undefined)
                        }
                      }}
                      className={cn(
                        "w-full p-4 text-left bg-background transition-colors",
                        isActive && "bg-accent/40 ring-1 ring-inset ring-border",
                        swipingItem?.id !== conversationId && "hover:bg-muted/50",
                        swipingItem?.id === conversationId && "focus:outline-hidden active:outline-hidden"
                      )}
                      style={{
                        transform: swipingItem?.id === conversationId ? `translateX(${swipingItem.offset}px)` : "translateX(0)",
                        transition: swipingItem?.id === conversationId ? "none" : "transform 0.3s ease, background-color 0.2s ease",
                      }}
                      onTouchStart={(e) => handleTouchStart(e, conversationId)}
                      onTouchMove={(e) => handleTouchMove(e, conversationId)}
                      onTouchEnd={() => handleTouchEnd(conversationId)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="h-10 w-10 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                            {avatar ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={avatar} alt={name} className="w-full h-full object-cover" />
                            ) : (
                              <User className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          {isUnread && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full border-2 border-background"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className={cn(
                              "text-sm truncate",
                              isUnread ? "font-bold text-foreground" : "font-semibold"
                            )}>{name}</h3>
                            {last?.created_at && (
                              <div className="flex items-center gap-2 md:pr-10">
                                <span className={cn(
                                  "text-xs",
                                  isUnread ? "text-foreground font-medium" : "text-muted-foreground"
                                )}>
                                  {formatConversationTimestamp(last.created_at)}
                                </span>
                                {isUnread && (
                                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                                )}
                              </div>
                            )}
                          </div>
                          <p className={cn(
                            "text-sm truncate md:pr-10",
                            isUnread ? "text-foreground font-medium" : "text-muted-foreground"
                          )}>
                            {last?.content || t("startTheConversation")}
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                )
              })}
            </div>
          )
  })()

  return (
    <div className="flex h-full min-h-0 flex-col">
      {layout === "split" ? (
        <div className="min-h-0 flex-1 overflow-y-auto" onClick={handleInboxClick}>
          <div className="sticky top-0 z-30 isolate">
            <div aria-hidden style={inboxFloatingSearchFadeStyle} className={inboxFloatingSearchFadeLayerClassName} />
            <div className="relative z-10 px-4 pb-3 pt-3">{searchBlock}</div>
          </div>
          {conversationList}
        </div>
      ) : (
        <>
          <div className="space-y-4 border-b p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                <h2 className="text-lg font-semibold">{t("messages")}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8 rounded-full bg-transparent"
                  onClick={() => {
                    if (onComposeAction) onComposeAction()
                    setSearch("")
                  }}
                >
                  <Edit3 className="h-4 w-4" />
                </Button>
                <Badge variant="secondary" className="rounded-full px-2 py-1 text-[10px]">
                  {t("beta")}
                </Badge>
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => onCloseAction?.()} aria-label={t("close")}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {searchBlock}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" onClick={handleInboxClick}>
            {conversationList}
          </div>
        </>
      )}
      {reportTarget ? (
        <ReportDialog
          open={true}
          onOpenChangeAction={(next) => {
            if (!next) setReportTarget(null)
          }}
          itemType={reportTarget.itemType}
          itemId={reportTarget.itemId}
          itemTitle={reportTarget.itemTitle}
          currentUserId={currentUserId}
          reportedUserId={reportTarget.reportedUserId}
          reportedUsername={reportTarget.reportedUsername}
          reportedAvatarUrl={reportTarget.reportedAvatarUrl}
        />
      ) : null}
    </div>
  )
}
